import { DataGrid } from '@mui/x-data-grid';
import {
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { ApiServices } from '../../utils/ApiService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ExpenseGridPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [yaazhBranches, setYaazhBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("All Branches");

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // ---------------- FETCH DATA ----------------
  const fetchData = async () => {
    try {
    setLoading(true);
      const res = await ApiServices.get(`expenses/list/${user.id}`);
      const data = res?.data || [];

      const branches = data?.map(item => item?.branch);
      const uniqueBranches = [...new Set(branches)];
      setYaazhBranches(uniqueBranches)

      const formatted = data.map((item) => ({
        ...item,
        id: Number(item.id),
        emp_id: Number(item.emp_id),
        requested_amount: Number(item.requested_amount),
        approved_amount: item.approved_amount
          ? Number(item.approved_amount)
          : null,
      }));

      setRows(formatted);
    } catch (err) {
      console.error(err);
      setRows([]);
    }
    finally{
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  // ---------------- SEARCH FILTER ----------------
  const filteredRows = rows?.filter((row) => {
    const value = search.toLowerCase();

    const matchesSearch =
      row.emp_name?.toLowerCase().includes(value) ||
      row.category?.toLowerCase().includes(value) ||
      row.sub_category?.toLowerCase().includes(value) ||
      row.status?.toLowerCase().includes(value);

    const matchesBranch = selectedBranch === "All Branches" || row.branch === selectedBranch;

    return matchesSearch && matchesBranch;
  });

  // ---------------- EXPORT EXCEL ----------------
  const handleExport = () => {
    const exportData = rows.map((row) => ({
      Emp_ID: row.emp_id,
      Emp_Name: row.emp_name,
      Category: row.category,
      Sub_Category: row.sub_category,
      Requested: row.requested_amount,
      Approved: row.approved_amount,
      Status: row.status,
      Verified_By: row.verified_by_name,
      Verified_At: row.verified_at
        ? new Date(row.verified_at).toLocaleString('en-GB')
        : '',
      Approved_By: row.approved_by_name,
      Approved_At: row.approved_at
        ? new Date(row.approved_at).toLocaleString('en-GB')
        : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const fileData = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });

    saveAs(fileData, 'Expenses.xlsx');
  };

  // ---------- FORMAT CURRENCY ----------
  const formatCurrency = (value) => {
    if (value == null) return '';
    return `₹ ${Number(value).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // ---------------- COLUMNS ----------------
  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },

    // 🔥 Combined Emp ID + Name
    {
      field: 'emp_name',
      headerName: 'Requestor',
      width: 180,
      renderCell: (params) => {
        const { emp_id, emp_name } = params.row;
        return `${emp_id} - ${emp_name || ''}`;
      },
    },

    {
      field: 'requested_at',
      headerName: 'Date Time',
      width: 180,
      renderCell: (params) => {
        const value = params?.row?.created_at;
        if (!value) return '';
        return new Date(value).toLocaleString();
      },
    },

    { field: 'category', headerName: 'Category', width: 150 },
    //{ field: 'sub_category', headerName: 'Sub Category', width: 150 },

    {
      field: 'requested_amount',
      headerName: 'Requested',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params?.row?.requested_amount),
    },

    // {
    //   field: 'approved_amount',
    //   headerName: 'Approved',
    //   width: 150,
    //   align: 'right',
    //   headerAlign: 'right',
    //   renderCell: (params) => formatCurrency(params?.row?.approved_amount),
    // },

    // 🔥 STATUS COLOR
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params) => {
        const status = params.value;

        let bg = '#e0e0e0';

        if (status === 'PENDING') bg = '#ffe0b2';
        else if (status === 'VERIFIED') bg = '#bbdefb';
        else if (status === 'APPROVED') bg = '#c8e6c9';
        else if (status?.includes('REJECTED')) bg = '#ffcdd2';

        return (
          <span
            style={{
              backgroundColor: bg,
              padding: '4px 10px',
              borderRadius: '12px',
              fontWeight: 600,
            }}
          >
            {status}
          </span>
        );
      },
    },

    // { field: 'verified_by_name', headerName: 'Verified By', width: 150 },

    // {
    //   field: 'verified_at',
    //   headerName: 'Verified At',
    //   width: 180,
    //   renderCell: (params) => {
    //     const value = params?.row?.verified_at;
    //     if (!value) return '';
    //     return new Date(value).toLocaleString();
    //   },
    // },

    { field: 'approved_by_name', headerName: 'Completed By', width: 150 },

    {
      field: 'approved_at',
      headerName: 'Completed At',
      width: 180,
      renderCell: (params) => {
        const value = params?.row?.approved_at;
        if (!value) return '';
        return new Date(value).toLocaleString();
      },
    },
  ];

  return (
    <Grid item xs={12} style={{ width: "100%" }}>
      <Paper elevation={3} style={{ padding: 16 }}>

        {/* HEADER */}
        <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
          <Grid item>
            <Typography variant="h6">Expenses</Typography>
          </Grid>

          <Grid item xs={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>

          <Grid item>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Branch</InputLabel>
              <Select
                value={selectedBranch}
                label="Branch"
                onChange={(e) => setSelectedBranch(e.target.value)}
              >
                <MenuItem value="All Branches">All Branches</MenuItem>

                {yaazhBranches?.map((branch) => (
                  <MenuItem key={branch} value={branch}>
                    {branch}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item>
            {/* <Button
              variant="contained"
              color="success"
              onClick={handleExport}
              sx={{ mr: 1 }}
            >
              Export
            </Button> */}

            <Button
              variant="contained"
              onClick={() => navigate("/expenses/ExpenseDynamicPage")}
            >
              + Add Expense
            </Button>
          </Grid>
        </Grid>

        {/* GRID */}
        <div style={{ height: 500, width: "100%", marginTop: 20 }}>

          {loading ? (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ height: "100%" }}
            >
              <CircularProgress />
            </Grid>
          ) : (
            <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.id}
                pageSize={5}
                rowsPerPageOptions={[5]}
            pageSizeOptions={[10, 20, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            sortingOrder={['asc', 'desc']}
            onRowDoubleClick={(params) => {
              navigate(`/expenses/view/${params.row.id}`);
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
            }}
          />
        )}
        </div>

      </Paper>
    </Grid>
  );
};

export default ExpenseGridPage;